import React, { useContext, useState, useEffect } from "react";
import { Table, Menu, Dropdown } from "antd";
import { DataContext } from "../DataContext";
import Filters from "../Filters";
import EditTaskModal from "../Modals/EditTaskModal";
import PreviewTaskModal from "../Modals/PreviewTaskModal";
import DeleteComponent from "../../../../components/DeleteComponent/DeleteComponent";
import { httpService } from "../../../../../services/httpService.service";
import MessagePopUp from "../../../../components/Messages/MessagePopUp";
import editIcon from "../../../../../Assets/svg/table-action-edit.svg";
import exclamationMark from "../../../../../Assets/svg/exclamationMark.svg";
import previewIcon from "../../../../../Assets/svg/table-action-preview.svg";
import deleteIcon from "../../../../../Assets/svg/table-action-delete.svg";
import { RolesEnum } from "../../../../../services/rolesEnum";
import { isAuthorized } from "../../../../../services/authService";
import IconStrategy from "../../../../../Assets/svg/task.svg";
import { formatDate } from "../../../../../services/helpFunctions/formatDateAndTime";
import {
  getTaskStatus,
  taskStatusesEnum,
} from "../../../../../services/Enums/taskStatusesEnum";

const taskTypes = [
  { id: 1, name: "Ops preparation" },
  { id: 2, name: "Data Collection" },
  { id: 3, name: "Data check" },
  { id: 4, name: "Translation" },
  { id: 5, name: "Field report" },
  { id: 6, name: "Data cleaning" },
  { id: 7, name: "Data analysis" },
  { id: 8, name: "Data translation" },
  { id: 9, name: "IP report" },
  { id: 10, name: "Donor report" },
  { id: 11, name: "Other" },
];

const TableView = ({ dataUpdated, setDataUpdated }) => {
  const context = useContext(DataContext);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState();
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 576);
  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 576);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const [message, setMessage] = useState({
    trigger: false,
    type: "",
    icon: "",
    title: "",
    text: "",
  });

  if (!context) {
    return <></>;
  }
  const {
    data,
    loading,
    setLoading,
    fetchData,
    changeTaskStatus,
    filters,
    pagination,
    setPagination,
  } = context;

  if (dataUpdated) {
    fetchData();
    setDataUpdated(false);
  }
  const handleStatusChange = (record, key) => {
    if (
      isAuthorized([
        RolesEnum.ProjectManager,
        RolesEnum.SystemAdmin,
        RolesEnum.FieldCoordinator,
        RolesEnum.FieldTeamLeader,
        RolesEnum.FieldEnumerator,
        RolesEnum.FieldLogistics,
        RolesEnum.DataAnalyst,
      ])
    ) {
      changeTaskStatus(record.id, key);
    } else {
      setMessage({
        trigger: true,
        type: "danger",
        icon: exclamationMark,
        title: "Unauthorized!",
        text: "You do not have permission to change the status.",
      });
    }
  };

  const columns = [
    {
      title: <span className="styledTitle">Task Identifier</span>,
      dataIndex: ["taskSite", "identifier"],
      key: "identifier",
      sorter: (a, b) => a.identifier.localeCompare(b.identifier),
      render: (text, record) => (
        <span className="styledDataIndex3">{record.identifier}</span>
      ),
    },
    {
      title: <span className="styledTitle">Task Name</span>,
      dataIndex: ["taskSite", "name"],
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <span className="styledDataIndex3">{record?.name}</span>
      ),
    },
    {
      title: <span className="styledTitle">Status</span>,
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => {
        const statusA = a.status ? a.status.toString() : "";
        const statusB = b.status ? b.status.toString() : "";
        return statusA.localeCompare(statusB, undefined, {
          sensitivity: "base",
        });
      },
      render: (text, record) => {
        const status = getTaskStatus(text);
        // Check if the user is authorized to change status
        const isUserAuthorized = isAuthorized([
          RolesEnum.ProjectManager,
          RolesEnum.SystemAdmin,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldTeamLeader,
          RolesEnum.FieldEnumerator,
          RolesEnum.FieldLogistics,
          RolesEnum.DataAnalyst,
        ]);

        return (
          <Dropdown
            overlay={statusMenu(record)}
            trigger={["click"]}
            disabled={!isUserAuthorized} // Disable if the user is not authorized
          >
            <div
              className={status.className}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: isUserAuthorized ? "pointer" : "not-allowed", // Change cursor based on authorization
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8px"
                height="8px"
                viewBox="0 0 9 9"
              >
                <circle cx="4.5" cy="4.27344" r="4" fill={status.fill} />
              </svg>
              <span className="styledDataIndex3">{status.text}</span>
            </div>
          </Dropdown>
        );
      },
    },
    {
      title: <span className="styledTitle">Deadline</span>,
      dataIndex: "deadline",
      key: "deadline",
      width: 250,
      sorter: (a, b) => a.deadline.localeCompare(b.deadline),
      render: (text) => (
        <span className="styledDataIndex3">{formatDate(text)}</span>
      ),
    },
    {
      title: <span className="styledTitle">Assigned To</span>,
      dataIndex: "assignees", // Directly access the top-level assignees array
      key: "assignedTo",
      sorter: (a, b) => {
        // Sort based on concatenated names or fallback to email
        const aNames = Array.isArray(a.assignees)
          ? a.assignees.map((user) => user.firstName || user.email).join(", ")
          : "";
        const bNames = Array.isArray(b.assignees)
          ? b.assignees.map((user) => user.firstName || user.email).join(", ")
          : "";
        return aNames.localeCompare(bNames);
      },
      render: (assignees) => {
        // Render logic for assignees array
        if (Array.isArray(assignees) && assignees.length > 0) {
          return assignees
            .map((user) =>
              user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.email || "Unknown User"
            )
            .join(", ");
        }
        return "No Assignees";
      },
    },

    {
      title: <span className="styledTitle">Type</span>,
      dataIndex: "type",
      key: "type",
      sorter: (a, b) => {
        const typeA = a.type ? a.type.toString() : "";
        const typeB = b.type ? b.type.toString() : "";
        return typeA.localeCompare(typeB, undefined, { sensitivity: "base" });
      },
      render: (typeId) => {
        const taskType = taskTypes.find((task) => task.id === typeId);
        return (
          <span className="styledDataIndex3">
            {taskType ? taskType.name : "Unknown Type"}
          </span>
        );
      },
    },
    {
      key: "action",
      className: !isAuthorized([
        RolesEnum.SystemAdmin,
        RolesEnum.ProjectManager,
        RolesEnum.FieldCoordinator,
        RolesEnum.FieldLogistics,
        RolesEnum.DataAnalyst,
        RolesEnum.FieldTeamLeader,
      ])
        ? "d-none"
        : "",
      render: (text, record) => (
        <div className="actionDiv">
          {isAuthorized([
            RolesEnum.ProjectManager,
            RolesEnum.SystemAdmin,
            RolesEnum.FieldCoordinator,
            RolesEnum.FieldTeamLeader,
            RolesEnum.FieldEnumerator,
            RolesEnum.FieldLogistics,
            RolesEnum.DataAnalyst,
          ]) && (
            <img
              src={previewIcon}
              onClick={() => handlePreview(record)}
              style={{ cursor: "pointer" }}
              alt="Preview"
            />
          )}
          {isAuthorized([
            RolesEnum.ProjectManager,
            RolesEnum.SystemAdmin,
            RolesEnum.FieldCoordinator,
            RolesEnum.FieldTeamLeader,
            RolesEnum.FieldLogistics,
            RolesEnum.DataAnalyst,
          ]) && (
            <img
              src={editIcon}
              onClick={() => handleEdit(record)}
              style={{ cursor: "pointer" }}
              alt="Edit"
            />
          )}
          {isAuthorized([RolesEnum.ProjectManager, RolesEnum.SystemAdmin]) && (
            <img
              src={deleteIcon}
              onClick={() => handleDelete(record)}
              style={{ cursor: "pointer" }}
              alt="Delete"
            />
          )}
        </div>
      ),
      fixed: isLargeScreen && data.length > 0 ? "right" : undefined,
    },
  ];

  const statusMenu = (record) => (
    <Menu onClick={({ key }) => handleStatusChange(record, key)}>
      {Object.entries(taskStatusesEnum)
        .filter(([key]) => key !== "default")
        .map(([key, value]) => (
          <Menu.Item key={key}>{value.text}</Menu.Item>
        ))}
    </Menu>
  );

  const handlePreview = (record) => {
    setSelectedTaskId(record.id);
    setShowPreviewModal(true);
  };

  const handleEdit = (record) => {
    setSelectedTaskId(record.id);
    setShowEditModal(true);
  };

  const handleDelete = async (record) => {
    setSelectedTaskId(record.id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    if (selectedTaskId) {
      const requestBody = [{ id: selectedTaskId }];
      const url = `/api/Tasks/deleteTasks?includeIsDeleted=false`;

      httpService.delete(
        url,
        requestBody,
        (response) => {
          const message =
            response.data?.message ||
            "The task was successfully deleted from your database.";

          setMessage({
            trigger: true,
            type: "success",
            icon: IconStrategy,
            title: "Task deleted successfully!",
            text: message,
          });
          fetchData();
          setLoading(false);
          setShowDeleteModal(false);
        },
        (error) => {
          console.error("Error deleting record:", error);
          const errorMessage =
            error?.errors?.[0]?.message ||
            error?.message ||
            "We had a problem deleting this task, please try again.";

          setMessage({
            trigger: true,
            type: "danger",
            icon: IconStrategy,
            title: "Oops! A problem has occurred!",
            text: errorMessage,
          });
          fetchData();
          setLoading(false);
          setShowDeleteModal(false);
        }
      );
    }
  };

  // const rowSelection = {
  //   onChange: (selectedRowKeys, selectedRows) => {
  //     console.log(
  //       `selectedRowKeys: ${selectedRowKeys}`,
  //       "selectedRows: ",
  //       selectedRows
  //     );
  //   },
  //   onSelect: (record, selected, selectedRows) => {
  //     console.log(record, selected, selectedRows);
  //   },
  //   onSelectAll: (selected, selectedRows, changeRows) => {
  //     console.log(selected, selectedRows, changeRows);
  //   },
  // };

  const handleTableChange = (current, pageSize) => {
    setPagination({
      ...pagination,
      current: current,
      pageSize: pageSize,
    });
  };

  return (
    <div className="d-flex gap-3 flex-column">
      <MessagePopUp
        trigger={message.trigger}
        type={message.type}
        icon={message.icon}
        messageTitle={message.title}
        messageText={message.text}
      />
      <div className="mb-4 mt-5">
        <Filters setPagination={setPagination} />
      </div>
      <Table
        loading={loading}
        dataSource={data}
        // rowSelection={{ ...rowSelection }}
        columns={data?.length > 0 ? columns : []}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: handleTableChange,
        }}
      />

      {selectedTaskId && (
        <>
          <EditTaskModal
            visible={showEditModal}
            setShowEditModal={setShowEditModal}
            taskId={selectedTaskId}
            setMessage={setMessage}
          />
          <PreviewTaskModal
            isVisible={showPreviewModal}
            onClose={() => setShowPreviewModal(false)}
            taskId={selectedTaskId}
          />
          <DeleteComponent
            visible={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            text={
              "You're about to delete this task. This action can’t be undone. "
            }
            header={"Delete "}
            onConfirm={handleConfirmDelete}
            loading={loading}
          />
        </>
      )}
    </div>
  );
};

export default TableView;
